import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, History, X, TrendingUp, LayoutGrid, Mic } from "lucide-react";
import { toast } from "sonner";
import { api, img, inr } from "../lib/api";
import { productPath } from "../lib/seo";
import { t, lang } from "../lib/i18n";

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

const KEY = "ez_recent_q";
const load = () => JSON.parse(localStorage.getItem(KEY) || "[]");
const row = "w-full flex items-center gap-2 px-2 py-2 rounded-xl text-sm text-left hover:bg-white/5";

export default function SearchBox({ className, testid, placeholder }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [recent, setRecent] = useState(load);
  const [sug, setSug] = useState(null);
  const timer = useRef();
  const nav = useNavigate();
  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) return setSug(null);
    timer.current = setTimeout(() => api.get("/search/suggest", { params: { q: q.trim() } }).then((r) => setSug(r.data)).catch(() => {}), 200);
  }, [q]);
  const remember = (v) => { const next = [v, ...recent.filter((x) => x.toLowerCase() !== v.toLowerCase())].slice(0, 8); localStorage.setItem(KEY, JSON.stringify(next)); setRecent(next); };
  const go = (term) => {
    const v = term.trim();
    if (!v) return;
    remember(v); setOpen(false); setQ(v);
    nav(`/products?q=${encodeURIComponent(v)}`);
  };
  const pick = (to, label) => { remember(label); setOpen(false); nav(to); };
  const [listening, setListening] = useState(false);
  const listen = () => {
    const r = new SR();
    r.lang = lang() === "te" ? "te-IN" : "en-IN";
    r.interimResults = false;
    r.onresult = (e) => { const said = e.results[0][0].transcript; setQ(said); go(said); };
    r.onerror = () => toast.error("Couldn't hear you. Allow microphone access and try again.");
    r.onend = () => setListening(false);
    setListening(true);
    r.start();
  };
  const clear = () => { localStorage.removeItem(KEY); setRecent([]); };
  const shown = recent.filter((x) => x.toLowerCase().includes(q.trim().toLowerCase())).slice(0, sug ? 3 : 8);
  const hasSug = sug && (sug.terms.length || sug.products.length || sug.categories.length);
  const keep = (e) => e.preventDefault();
  return (
    <form onSubmit={(e) => { e.preventDefault(); go(q); }} className={`relative ${className}`} data-testid={`${testid}-form`}>
      <Search size={16} className="text-slate-400 shrink-0" />
      <input data-testid={testid} type="search" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder} autoComplete="off" className="bg-transparent outline-none text-sm flex-1 min-w-0 placeholder:text-slate-500" />
      {SR && <button type="button" data-testid={`${testid}-mic`} aria-label="Search by voice" onClick={listen} className={`p-1.5 rounded-full transition-colors ${listening ? "bg-[#ff5500] text-white animate-pulse" : "text-slate-400 hover:text-white"}`}><Mic size={16} /></button>}
      {open && (shown.length > 0 || hasSug) && (
        <div data-testid={`${testid}-dropdown`} className="absolute left-0 right-0 top-full mt-2 z-[60] rounded-2xl bg-[#12141c] border border-white/10 p-2 shadow-2xl max-h-[70vh] overflow-y-auto">
          {shown.length > 0 && <div data-testid={`${testid}-recent`}>
            <div className="flex items-center justify-between px-2 pb-1 text-[11px] uppercase tracking-wider text-slate-500">
              {t("Recent searches")}<button type="button" data-testid={`${testid}-recent-clear`} onMouseDown={keep} onClick={clear} className="text-[#ff5500] normal-case">{t("Clear")}</button>
            </div>
            {shown.map((x) => (
              <button type="button" key={x} data-testid={`${testid}-recent-item`} onMouseDown={keep} onClick={() => go(x)} className={row}>
                <History size={14} className="text-slate-500" /><span className="flex-1 truncate">{x}</span>
                <X size={12} className="text-slate-600" onClick={(e) => { e.stopPropagation(); const n = recent.filter((r) => r !== x); localStorage.setItem(KEY, JSON.stringify(n)); setRecent(n); }} />
              </button>
            ))}
          </div>}
          {sug?.terms.map((s) => <button type="button" key={s} data-testid={`${testid}-term`} onMouseDown={keep} onClick={() => go(s)} className={row}><TrendingUp size={14} className="text-[#00f0ff]" /><span className="flex-1 truncate">{s}</span></button>)}
          {sug?.categories.map((c) => <button type="button" key={c.slug} data-testid={`${testid}-category`} onMouseDown={keep} onClick={() => pick(`/category/${c.slug}?q=${encodeURIComponent(q.trim())}`, q.trim())} className={row}><LayoutGrid size={14} className="text-[#ffb703]" /><span className="flex-1 truncate">{q.trim()} <span className="text-slate-500">in</span> <b className="text-[#ffb703]">{c.name}</b></span></button>)}
          {sug?.products.length > 0 && <p className="px-2 pt-2 pb-1 text-[11px] uppercase tracking-wider text-slate-500">Products</p>}
          {sug?.products.map((p) => (
            <button type="button" key={p.id} data-testid={`${testid}-product`} onMouseDown={keep} onClick={() => pick(productPath(p), p.title)} className={row}>
              <img src={img(p.image)} alt="" className="w-9 h-9 rounded-lg object-cover bg-white/5" /><span className="flex-1 truncate">{p.title}</span><span className="text-xs font-semibold">{inr(p.price)}</span>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}
